import { useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';

interface StopWorkerDialogProps {
  nodeAgentId: string;
  instanceId: string;
  workerName: string;
  onClose: () => void;
  onConfirm: () => void;
}

export default function StopWorkerDialog({
  nodeAgentId,
  instanceId,
  workerName,
  onClose,
  onConfirm,
}: StopWorkerDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await sendNodeCommand(nodeAgentId, 'stop_worker', { instance_id: instanceId });
      onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to stop worker:', err);
      setError('Failed to stop worker. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Stop Worker</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will forcefully stop worker <strong>{workerName}</strong>. The worker process will be killed and any
          running tasks will be interrupted.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="error" disabled={isSubmitting}>
          {isSubmitting ? 'Stopping\u2026' : 'Stop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
